import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Plus } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { api } from '../../lib/api';
import { formatAmount } from '../../lib/format';

interface Form1099Row {
  vendor_id: string;
  vendor_name: string;
  amount_cents: string | number;
  box: string;
  status: 'draft' | 'filed';
}

/**
 * Annual 1099-NEC builder for US contractor payables. Aggregates all paid
 * AP invoices above $600 in the selected tax year. The W-9/W-8 form upload
 * still lives on the vendor record itself; this page is purely the year-end
 * 1099 dashboard.
 */
export function TaxUsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear() - 1);

  const list = useQuery<Form1099Row[]>({
    queryKey: ['tax-us-1099', wsId, year],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get<Form1099Row[]>(`/workspaces/${wsId}/tax-us/1099?year=${year}`)).data,
  });

  const build = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ count: number }>(`/workspaces/${wsId}/tax-us/1099/build`, {
          taxYear: year,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-us-1099', wsId, year] });
    },
  });

  function downloadCsv() {
    const rows = list.data ?? [];
    if (!rows.length) return;
    const header = 'vendor_id,vendor_name,box,amount_usd,status\n';
    const body = rows
      .map((r) => {
        const usd = (Number(r.amount_cents) / 100).toFixed(2);
        const safe = (s: string) => `"${(s ?? '').replace(/"/g, '""')}"`;
        return [r.vendor_id, safe(r.vendor_name), r.box, usd, r.status].join(',');
      })
      .join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `1099-NEC-${year}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const totalCents = (list.data ?? []).reduce((s, r) => s + Number(r.amount_cents), 0);
  const years = [now.getUTCFullYear(), now.getUTCFullYear() - 1, now.getUTCFullYear() - 2];

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="display text-2xl font-semibold">{t('taxUs.title')}</h1>
            <p className="text-sm text-ink-500 mt-1">{t('taxUs.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => build.mutate()}
              disabled={build.isPending}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 text-sm font-medium disabled:opacity-50"
            >
              <Plus size={14} />
              {t('taxUs.build')}
            </button>
            <button
              type="button"
              onClick={downloadCsv}
              disabled={!list.data?.length}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium disabled:opacity-40"
            >
              <FileText size={14} />
              {t('taxUs.downloadCsv')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <Card className="p-4">
            <div className="text-xs text-ink-500">{t('taxUs.totalContractors')}</div>
            <div className="num text-2xl font-semibold mt-1">{list.data?.length ?? 0}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-ink-500">{t('taxUs.totalPayouts')}</div>
            <div className="num text-2xl font-semibold mt-1">
              {formatAmount(totalCents, 'USD', 'en')}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-ink-500">{t('taxUs.threshold')}</div>
            <div className="num text-2xl font-semibold mt-1">$600.00</div>
          </Card>
        </div>

        <Card>
          <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 font-medium">
            {t('taxUs.contractors')} {year}
          </div>
          {list.isLoading ? (
            <div className="p-10 text-center text-ink-500">
              <Loader2 size={14} className="inline animate-spin mr-2" />
              {t('common.loading')}
            </div>
          ) : !list.data || list.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('taxUs.empty')}</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-ink-500 border-b border-ink-200 dark:border-ink-800">
                  <th className="px-5 py-3 font-medium">{t('taxUs.vendor')}</th>
                  <th className="px-5 py-3 font-medium">{t('taxUs.box')}</th>
                  <th className="px-5 py-3 font-medium">{t('common.status')}</th>
                  <th className="px-5 py-3 font-medium text-right">{t('common.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r) => (
                  <tr key={r.vendor_id} className="border-b border-ink-200 dark:border-ink-800">
                    <td className="px-5 py-3">{r.vendor_name}</td>
                    <td className="px-5 py-3 font-mono text-xs">{r.box}</td>
                    <td className="px-5 py-3">
                      <Pill tone={r.status === 'filed' ? 'success' : 'neutral'}>
                        {t(`taxUs.statuses.${r.status}`)}
                      </Pill>
                    </td>
                    <td className="px-5 py-3 text-right num">
                      {formatAmount(r.amount_cents, 'USD', 'en')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <p className="text-xs text-ink-500 mt-4">{t('taxUs.footnote')}</p>
      </div>
    </AppShell>
  );
}
